// Admin API helper functions

const TOKEN_KEY = 'tedra_admin_token';
const USER_KEY = 'tedra_admin_user';

export const getStoredToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
  } catch (_) {
    return null;
  }
};

export const getStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

export const setAuthData = (token, username, remember = true) => {
  const storage = remember ? localStorage : sessionStorage;
  storage.setItem(TOKEN_KEY, token);
  storage.setItem(USER_KEY, JSON.stringify({ username }));
};

export const clearAuthData = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
};

const authFetch = async (url, options = {}) => {
  const token = getStoredToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    clearAuthData();
    window.dispatchEvent(new Event('tedra_auth_logout'));
    throw new Error('Session expired. Please log in again.');
  }

  if (!response.ok) {
    let errorMsg = `HTTP Error ${response.status}`;
    try {
      const errData = await response.json();
      if (errData.detail) errorMsg = errData.detail;
    } catch (_) {}
    throw new Error(errorMsg);
  }

  return response.json();
};

export const adminLogin = async (username, password, remember = true) => {
  const res = await fetch('/api/admin/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  if (!res.ok) {
    let msg = 'Authentication failed';
    try {
      const data = await res.json();
      if (data.detail) msg = data.detail;
    } catch (_) {}
    throw new Error(msg);
  }

  const data = await res.json();
  setAuthData(data.access_token, data.username, remember);
  return data;
};

export const getAdminMe = async () => {
  return authFetch('/api/admin/me');
};

export const getAnalyticsOverview = async () => {
  return authFetch('/api/admin/analytics/overview');
};

export const getAnalyticsFaqs = async (limit = 15) => {
  return authFetch(`/api/admin/analytics/faqs?limit=${limit}`);
};

export const getSessions = async (page = 1, pageSize = 20, search = '', status = '') => {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  if (search) params.append('search', search);
  if (status) params.append('status', status);

  return authFetch(`/api/admin/sessions?${params.toString()}`);
};

export const getSessionDetails = async (sessionId) => {
  return authFetch(`/api/admin/sessions/${encodeURIComponent(sessionId)}`);
};

export const downloadCsvExport = async () => {
  const token = getStoredToken();
  const response = await fetch('/api/admin/analytics/export', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) throw new Error('Export failed');

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tedra_conversations_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
